import { CloseOutlined } from "@ant-design/icons";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Snackbar,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import MessageDownloader from "../../components/common/DownloadMessages";
import Conversation from "../../components/Conversation";
import ProfileDetails from "../../components/ProfileDetails";
import { UserApiService } from "../../services/api/UserAPIService";
import {
  API_ERROR_MESSAGE,
  API_SUCCESS_MESSAGE,
  BUTTON,
  PAGE_HEADING,
} from "../../shared/constants";
import TaskBoard from "../TodoList/TaskBoard";
import MessageView from "./messagesView";
import MemoryRender from "../Memory_journal/MemoryRender";
import UserPaymentUnsubscribe from "../Payments/UserPaymentUnsubscribe";

const UserDetails = () => {
  const location = useLocation();
  const { data } = location.state || {};
  const [messages, setMessages] = useState([]);
  const [messageMeta, setMessageMeta] = useState("");
  const [loading, setLoading] = useState(true);
  const [openPopup, setOpenPopup] = useState(null);
  const [snackData, setSnackData] = useState({
    show: false,
    message: "",
    type: "error",
  });

  const extractGestationalAge = (dueDateStr) => {
    if (!dueDateStr) return "N/A";

    const dueDate = dayjs(dueDateStr);
    if (!dueDate.isValid()) return "Invalid Date";

    const today = dayjs();
    const daysUntilDue = dueDate.diff(today, "day");
    const gestationDays = 279 - daysUntilDue;

    if (gestationDays < 0) return "Not Pregnant Yet";
    if (gestationDays > 280) return "Post Due";

    const weeks = Math.floor(gestationDays / 7);
    const days = gestationDays % 7;

    return `${weeks} week${weeks !== 1 ? "s" : ""} ${days} day${
      days !== 1 ? "s" : ""
    }`;
  };

  const userData = {
    name: data?.name,
    phone: data?.phone,
    dueDate: data?.due_date || "XXX",
    gestationalAge: extractGestationalAge(data?.due_date),
    dateRegistered: data?.activated_on || "XXX",
    smsSent: data?.chat_count || "XX",
    status: data?.status,
    lastReceipt: data?.receiptTime,
  };

  const buttonLabel = {
    // INTENT_CLASSIFICATION: BUTTON.INTENT_CLASSIFICATION,
    DOCTOR_QUESTION_LIST: BUTTON.DOCTOR_QUESTION_LIST,
    TODO_LIST: BUTTON.TODO_LIST,
    SCHEDULED_MESSAGES: BUTTON.SCHEDULED_MESSAGES,
    MEMORY_JOURNAL: BUTTON.MEMORY_JOURNAL,
    USER_PREFERENCES: BUTTON.USER_PREFERENCES,
    USER_SHOPPING: BUTTON.USER_SHOPPING,
    UNSUBSCRIBE:BUTTON.UNSUBSCRIBE,
  };

  useEffect(() => {
    fetchUserConversation();
  }, [data]);

  const fetchUserConversation = () => {
    setLoading(true);
    let page = 1;
    let pagesize = 999;
    UserApiService.userConversation(data?.phone, page, pagesize)
      .then((response) => {
        setSnackData({
          show: true,
          message: response.message || API_SUCCESS_MESSAGE.USER_CONVERSATON,
          type: "success",
        });

        setMessages(response?.messages);
        setMessageMeta(response?.meta);
      })
      .catch((errResponse) => {
        setSnackData({
          show: true,
          message:
            errResponse?.response?.data?.message ||
            API_ERROR_MESSAGE.INTERNAL_SERVER_ERROR,
          type: "error",
        });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleOpen = (action) => {
    setOpenPopup(action);
  };

  const handleClose = () => {
    setOpenPopup(null);
  };

  const renderPopupContent = () => {
    switch (openPopup) {
      case buttonLabel.INTENT_CLASSIFICATION:
        return <div>Intent Classification content here</div>;
      case buttonLabel.DOCTOR_QUESTION_LIST:
        return <TaskBoard access="admin" type="inquires" />;
      case buttonLabel.TODO_LIST:
        return <TaskBoard access="admin" type="tasks" />;
      case buttonLabel.SCHEDULED_MESSAGES:
        return <MessageView phone={data?.phone}/>;
        case buttonLabel.MEMORY_JOURNAL:
        return <MemoryRender user_phone={data?.phone}/>;
      case buttonLabel.USER_PREFERENCES:
        return <div>User Preferences content here</div>;
      case buttonLabel.USER_SHOPPING:
        return <div>User Shopping content here</div>;
        case buttonLabel.UNSUBSCRIBE:
          return <UserPaymentUnsubscribe open = {openPopup} onClose={handleClose} phone={data?.phone} payment_customer_id={data?.payment_customer_id}/>;
      default:
        return null;
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <ProfileDetails userData={userData} />

      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="50vh"
        >
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={9}>
            <Box
              mt={2}
              p={2}
              sx={{
                border: "1px solid #ccc",
                borderRadius: "8px",
                borderTop: "2px solid #005CFF",
              }}
            >
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{ mb: 1, fontSize: "16px" }}
              >
                {PAGE_HEADING.CONVERSATION_HISTORY}
              </Typography>
              <Conversation
                initialMessages={messages}
                phone={data?.phone}
                totalMessages={messageMeta}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Box
              mt={2}
              sx={{
                display: "grid",
                gap: 1,
                padding: 2,
                border: "0.5px solid #ccc",
                borderTop: "2px solid #005CFF",
                height: "480px",
              }}
            >
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{ fontSize: "16px" }}
              >
                {PAGE_HEADING.ACTIONS}
              </Typography>
              {Object.values(buttonLabel).map((action) => (
                <Button
                  key={action}
                  variant="contained"
                  sx={{
                    bgcolor: "#005CFF",
                    color: "white",
                    textTransform: "none",
                    m: 1,
                  }}
                  onClick={() => handleOpen(action)}
                >
                   {action}
                </Button>
              ))}
              <MessageDownloader
                phone={userData?.phone}
                loading={loading}
                page="admin"
              />
            </Box>
          </Grid>
        </Grid>
      )}

      <Dialog open={Boolean(openPopup)} onClose={handleClose} fullWidth={false}
  maxWidth="lg"
      PaperProps={{
        sx: {
          width: {
            xs: "90vw",
            sm: openPopup === "Memory Journal" ? "100%" : "400px",
            md: openPopup === "Memory Journal" ? "100%" : "500px",
          },
          borderRadius: 3,
        }
      }}
      >
      <DialogTitle
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      pr: 2,
    }}
  >
    {openPopup}
    <IconButton onClick={handleClose} size="small">
      <CloseOutlined />
    </IconButton>
  </DialogTitle>
        <DialogContent sx={{background:"#f8fafd!important"}}>{renderPopupContent()}</DialogContent>
      </Dialog>
      <Snackbar
        style={{ top: "80px" }}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={snackData.show}
        autoHideDuration={3000}
        onClose={() => setSnackData({ show: false })}
      >
        <Alert
          onClose={() => setSnackData({ show: false })}
          severity={snackData.type}
        >
          {snackData.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserDetails;
